import { useState, useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './UseCases.css'



const USE_CASES = [
    {
        id: 'agri', label: 'Agriculture',
        accent: '#2d6a4f', rgb: '45,106,79',
        stat: '40%', statSub: 'input cost reduction',
        badge: 'GCS Agri',
        points: [
            'Automated spray mission planning',
            'NDVI crop health heatmap',
            'Variable rate application',
            'Compliance & operation logging',
        ],
        icon: (
            <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4">
                <path d="M24 4C24 4 8 16 8 28a16 16 0 0 0 32 0C40 16 24 4 24 4Z" />
                <path d="M24 4v40M8 28h32" strokeDasharray="3 2" opacity="0.35" />
                <circle cx="24" cy="28" r="3" fill="currentColor" stroke="none" />
            </svg>
        ),
    },
    {
        id: 'survey', label: 'Survey & Mapping',
        accent: '#4a6fa5', rgb: '74,111,165',
        stat: '2cm', statSub: 'ground accuracy',
        badge: 'GCS Survey',
        points: [
            'Photogrammetry grid flight planning',
            'GCP marker management on-site',
            'Corridor & linear mission support',
            'KML / GeoJSON export for GIS',
        ],
        icon: (
            <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4">
                <rect x="6" y="6" width="36" height="36" rx="2" />
                <path d="M6 18h36M6 30h36M18 6v36M30 6v36" opacity="0.3" />
                <circle cx="24" cy="24" r="3" fill="currentColor" stroke="none" />
            </svg>
        ),
    },
    {
        id: 'camera',
        label: 'Camera',
        accent: '#6b7280',
        rgb: '107,114,128',
        stat: '4K',
        statSub: 'photo & video capture',
        badge: 'Camera System',
        points: [
            'High-resolution photo capture',
            '4K stabilized video recording',
            'Real-time camera preview feed',
            'Gimbal control & angle adjustment',
        ],
        icon: (
            <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4">
                <rect x="10" y="14" width="28" height="20" rx="3" />
                <circle cx="24" cy="24" r="6" />
                <path d="M18 14l2-4h8l2 4" />
            </svg>
        ),
    },
    {
        id: 'inspection', label: 'Inspection',
        accent: '#5d179e', rgb: '93,23,158',
        stat: '70%', statSub: 'faster than manual',
        badge: 'DroneCommanderGCS',
        points: [
            'Repeatable saved inspection routes',
            'Hover-and-inspect waypoint mode',
            'Mid-flight GPS anomaly flagging',
            'Report-ready annotated flight logs',
        ],
        icon: (
            <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4">
                <path d="M24 6L6 18v24h36V18L24 6Z" />
                <path d="M16 42V28h16v14" />
                <circle cx="32" cy="16" r="4" />
                <path d="M35 19l4 4" strokeLinecap="round" />
            </svg>
        ),
    },
]

function Card({ data, index, isActive, isDimmed, onClick }) {
    const cardRef = useRef(null)

    // Cinematic card reveal from depth
    useEffect(() => {
        const el = cardRef.current
        if (!el) return
        gsap.fromTo(el,
            { opacity: 0, y: 80, rotateX: 20, z: -100, transformPerspective: 900 },
            {
                opacity: 1, y: 0, rotateX: 0, z: 0,
                duration: 1, ease: 'expo.out',
                delay: index * 0.12,
                scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'restart none none reverse' }
            }
        )
    }, [index])

    // 3D tilt on mouse move
    const onMouseMove = (e) => {
        const el = cardRef.current
        if (!el) return
        const { left, top, width, height } = el.getBoundingClientRect()
        const x = ((e.clientX - left) / width - 0.5) * 14
        const y = ((e.clientY - top) / height - 0.5) * -14
        gsap.to(el, {
            rotateY: x, rotateX: y,
            transformPerspective: 800,
            duration: 0.4, ease: 'power2.out',
        })
    }
    const onMouseLeave = () => {
        gsap.to(cardRef.current, {
            rotateY: 0, rotateX: 0,
            duration: 0.6, ease: 'expo.out',
        })
    }

    return (
        <button
            ref={cardRef}

            className={`uc-card ${isActive ? 'uc-card--active' : ''} ${isDimmed ? 'uc-card--dim' : ''}`}
            style={{ '--a': data.accent, '--rgb': data.rgb, opacity: 0 }}
            onClick={onClick}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <span className="uc-card-bg-num">{String(index + 1).padStart(2, '0')}</span>
            <div className="uc-icon" style={{ color: data.accent }}>{data.icon}</div>
            <h3 className="uc-label">{data.label}</h3>
            <div className="uc-stat">
                <span className="uc-stat-num" style={{ color: data.accent }}>{data.stat}</span>
                <span className="uc-stat-sub">{data.statSub}</span>
            </div>
            <div className="uc-bar" style={{ background: data.accent }} />
        </button>
    )
}

function Panel({ data, onClose }) {
    const panelRef = useRef(null)

    useEffect(() => {
        gsap.fromTo(panelRef.current,
            { opacity: 0, x: 40, rotateY: -8, transformPerspective: 1000 },
            { opacity: 1, x: 0, rotateY: 0, duration: 0.6, ease: 'expo.out' }
        )
    }, [data.id])

    return (
        <div ref={panelRef} className="uc-panel" style={{ '--a': data.accent, '--rgb': data.rgb }}>
            <button className="uc-close" onClick={onClose}>✕</button>
            <div className="uc-panel-stat">
                <span className="uc-panel-num" style={{ color: data.accent }}>{data.stat}</span>
                <span className="uc-panel-stat-sub">{data.statSub}</span>
            </div>
            <h3 className="uc-panel-label">{data.label}</h3>
            <ul className="uc-points">
                {data.points.map((pt, i) => (
                    <li key={i} className="uc-point" style={{ animationDelay: `${i * 0.07}s` }}>
                        <span className="uc-point-dot" style={{ background: data.accent }} />
                        {pt}
                    </li>
                ))}
            </ul>
            <div className="uc-badge" style={{ borderColor: data.accent }}>
                <span className="uc-badge-dot" style={{ background: data.accent }} />
                <span style={{ color: data.accent }}>{data.badge}</span>
            </div>
            <a href="/contact/support" className="uc-panel-cta" style={{ background: data.accent }}>
                Get in Touch →
            </a>
        </div>
    )
}

export default function UseCases() {
    const [activeId, setActiveId] = useState(null)
    const wrapRef = useRef(null)
    const headRef = useRef(null)
    const h2Ref = useRef(null)
    const active = USE_CASES.find(u => u.id === activeId) || null

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Eyebrow
            gsap.fromTo(headRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
                    scrollTrigger: { trigger: headRef.current, start: 'top 88%', toggleActions: 'restart none none reverse' }
                }
            )
            // H2 slams from depth
            gsap.fromTo(h2Ref.current,
                { opacity: 0, y: 80, rotateX: 25, transformPerspective: 800 },
                {
                    opacity: 1, y: 0, rotateX: 0, duration: 1.2, ease: 'expo.out',
                    scrollTrigger: { trigger: h2Ref.current, start: 'top 85%', toggleActions: 'restart none none reverse' }
                }
            )
        }, wrapRef)
        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} id="use-cases" className="uc-wrap">
            <div className="uc-bg-grid" />
            <div className="uc-inner">

                <div className="uc-header">
                    <span ref={headRef} className="uc-eyebrow" style={{ opacity: 0 }}>
                        <span className="uc-dot" />USE CASES
                    </span>
                    <h2 ref={h2Ref} className="uc-h2" style={{ opacity: 0 }}>
                        Built For<br /><em>Every Mission.</em>
                    </h2>
                </div>

                <div className={`uc-body ${active ? 'uc-body--open' : ''}`}>
                    <div className="uc-cards">
                        {USE_CASES.map((uc, i) => (
                            <Card
                                key={uc.id} data={uc} index={i}
                                isActive={activeId === uc.id}
                                isDimmed={!!activeId && activeId !== uc.id}
                                onClick={() => setActiveId(p => p === uc.id ? null : uc.id)}
                            />
                        ))}
                    </div>
                    {active && <Panel data={active} onClose={() => setActiveId(null)} />}
                </div>

                <p className="uc-hint">Click any use case to explore →</p>
            </div>
        </section>
    )
}